import { useEffect, useRef } from 'react';

export interface UseForegroundRefreshOptions {
  enabled?: boolean;
  minHiddenMs?: number;
  staleAfterMs?: number;
  cooldownMs?: number;
  onError?: (error: unknown) => void;
}

type RefreshTrigger = 'visibilitychange' | 'focus' | 'pageshow' | 'online';

const DEFAULT_MIN_HIDDEN_MS = 15_000;
const DEFAULT_STALE_AFTER_MS = 60_000;
const DEFAULT_COOLDOWN_MS = 2_000;

export function useForegroundRefresh(
  refresh: () => Promise<void>,
  options: UseForegroundRefreshOptions = {},
): void {
  const {
    enabled = true,
    minHiddenMs = DEFAULT_MIN_HIDDEN_MS,
    staleAfterMs = DEFAULT_STALE_AFTER_MS,
    cooldownMs = DEFAULT_COOLDOWN_MS,
    onError,
  } = options;

  const refreshRef = useRef(refresh);
  const onErrorRef = useRef(onError);
  const hiddenAtRef = useRef<number | null>(
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
      ? Date.now()
      : null,
  );
  const lastSuccessAtRef = useRef(Date.now());
  const lastTriggeredAtRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const isVisible = () => document.visibilityState === 'visible';

    const maybeRefresh = async (trigger: RefreshTrigger) => {
      if (!isVisible()) {
        return;
      }

      const now = Date.now();
      const hiddenAt = hiddenAtRef.current;
      const hiddenLongEnough =
        hiddenAt !== null && now - hiddenAt >= minHiddenMs;
      const isStale = now - lastSuccessAtRef.current >= staleAfterMs;
      const shouldRefresh =
        trigger === 'online' ? true : hiddenLongEnough || isStale;

      if (!shouldRefresh) {
        hiddenAtRef.current = null;
        return;
      }

      if (inFlightRef.current || now - lastTriggeredAtRef.current < cooldownMs) {
        return;
      }

      lastTriggeredAtRef.current = now;
      inFlightRef.current = true;

      try {
        await refreshRef.current();
        lastSuccessAtRef.current = Date.now();
      } catch (error) {
        if (onErrorRef.current) {
          onErrorRef.current(error);
        } else {
          console.error('Foreground refresh failed:', error);
        }
      } finally {
        inFlightRef.current = false;
        if (isVisible()) {
          hiddenAtRef.current = null;
        }
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }

      void maybeRefresh('visibilitychange');
    };

    const onFocus = () => {
      void maybeRefresh('focus');
    };

    const onPageShow = () => {
      void maybeRefresh('pageshow');
    };

    const onOnline = () => {
      if (isVisible()) {
        void maybeRefresh('online');
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', onOnline);
    };
  }, [cooldownMs, enabled, minHiddenMs, staleAfterMs]);
}

export default useForegroundRefresh;
